import { useEffect, useRef, useState } from "react";
import {
  Store, Upload, Rocket, Loader2, Pencil, Trash2,
  ChevronUp, ChevronDown, Plus, ExternalLink, ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useMyShop, useCreateShop, useUpdateShop, useDeployShop, useDeleteShop,
  useUploadShopLogo, useUploadShopHero,
  useShopCategories, useCreateCategory, useUpdateCategory,
  useDeleteCategory, useUploadCategoryImage, useReorderCategories,
  useShopProducts, useCreateProduct, useUpdateProduct,
  useDeleteProduct, useUploadProductImage,
  useUploadProductGalleryImage, useDeleteProductGalleryImage, useReorderProductGallery,
} from "@/hooks/useShop";
import type { ShopCategory, ShopProduct, ShopProductImage } from "@/types/api";
import { ProductVitrineModal } from "@/components/shared/ProductVitrineModal";
import { DeployConsentModal } from "@/components/shared/DeployConsentModal";

// ── Helpers ───────────────────────────────────────────────────────────────────

function apiErr(error: unknown): string {
  const e = error as { response?: { data?: { detail?: unknown } } };
  const detail = e?.response?.data?.detail;
  return Array.isArray(detail)
    ? detail.map((d: { msg?: string }) => d.msg).filter(Boolean).join(", ")
    : typeof detail === "string" ? detail : "Erro inesperado.";
}

// ── ImageUpload ───────────────────────────────────────────────────────────────

function ImageUpload({
  label, currentUrl, onFile, loading,
}: {
  label: string;
  currentUrl: string | null | undefined;
  onFile: (f: File) => void;
  loading?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        <div className="h-16 w-16 rounded-lg border bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
          {currentUrl
            ? <img src={currentUrl} alt={label} className="h-full w-full object-cover" />
            : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading}
          onClick={() => ref.current?.click()}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
          {currentUrl ? "Trocar" : "Enviar"}
        </Button>
        <input
          ref={ref}
          type="file"
          className="hidden"
          accept="image/jpeg,image/png,image/webp,image/svg+xml"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
            e.target.value = "";
          }}
        />
      </div>
    </div>
  );
}

// ── CreateShopForm ────────────────────────────────────────────────────────────

function CreateShopForm() {
  const [handle, setHandle] = useState("");
  const [name, setName] = useState("");
  const create = useCreateShop();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      { handle, name },
      {
        onSuccess: () => toast.success("Loja criada com sucesso!"),
        onError: (err) => toast.error(apiErr(err)),
      },
    );
  };

  return (
    <div className="max-w-md mx-auto mt-16">
      <Card>
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2 bg-primary/10 rounded-lg text-primary">
                <Store className="h-5 w-5" />
              </div>
              <CardTitle>Criar Loja</CardTitle>
            </div>
            <CardDescription>
              Configure sua loja de recompensas para indicadores.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="handle">Handle (URL da loja)</Label>
              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground whitespace-nowrap">shop.indiqr.com.br/</span>
                <Input
                  id="handle"
                  placeholder="minha-marca"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  required
                  pattern="[a-z0-9\-]+"
                />
              </div>
              <p className="text-xs text-muted-foreground">Somente letras minúsculas, números e hífens.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="shop-name">Nome da loja</Label>
              <Input
                id="shop-name"
                placeholder="Ex: Minha Marca"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={create.isPending}>
              {create.isPending
                ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Criando...</>
                : "Criar Loja"}
            </Button>
          </CardContent>
        </form>
      </Card>
    </div>
  );
}

// ── AparenciaTab ──────────────────────────────────────────────────────────────

function AparenciaTab({ handle, shop }: { handle: string; shop: ReturnType<typeof useMyShop>["data"] }) {
  const [form, setForm] = useState({
    name: shop?.name ?? "",
    tagline: shop?.tagline ?? "",
    hero_title: shop?.hero_title ?? "",
    hero_subtitle: shop?.hero_subtitle ?? "",
    instagram_url: shop?.instagram_url ?? "",
    whatsapp_url: shop?.whatsapp_url ?? "",
    email: shop?.email ?? "",
  });

  const update = useUpdateShop(handle);
  const uploadLogo = useUploadShopLogo(handle);
  const uploadHero = useUploadShopHero(handle);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = Object.fromEntries(
      Object.entries(form).filter(([, v]) => v !== ""),
    );
    update.mutate(payload, {
      onSuccess: () => toast.success("Aparência salva!"),
      onError: (err) => toast.error(apiErr(err)),
    });
  };

  return (
    <form onSubmit={handleSave} className="space-y-6 max-w-xl">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Nome da loja</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div className="space-y-2">
          <Label>Tagline</Label>
          <Input placeholder="Slogan curto" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Título do hero</Label>
          <Input placeholder="Bem-vindo à loja" value={form.hero_title} onChange={(e) => setForm({ ...form, hero_title: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Subtítulo do hero</Label>
          <Input placeholder="Resgate suas recompensas" value={form.hero_subtitle} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Instagram</Label>
          <Input placeholder="https://instagram.com/..." value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>
            WhatsApp <span className="text-muted-foreground font-normal text-xs">(só o número)</span>
          </Label>
          <Input
            placeholder="5511999999999"
            value={form.whatsapp_url}
            onChange={(e) => setForm({ ...form, whatsapp_url: e.target.value.replace(/[^\d+]/g, "") })}
          />
        </div>
        <div className="space-y-2">
          <Label>E-mail</Label>
          <Input placeholder="contato@..." value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <ImageUpload
          label="Logo"
          currentUrl={shop?.logo_url}
          loading={uploadLogo.isPending}
          onFile={(f) => uploadLogo.mutate(f, {
            onSuccess: () => toast.success("Logo atualizada!"),
            onError: (err) => toast.error(apiErr(err)),
          })}
        />
        <ImageUpload
          label="Imagem de fundo (hero)"
          currentUrl={shop?.hero_url}
          loading={uploadHero.isPending}
          onFile={(f) => uploadHero.mutate(f, {
            onSuccess: () => toast.success("Hero atualizado!"),
            onError: (err) => toast.error(apiErr(err)),
          })}
        />
      </div>

      <Button type="submit" disabled={update.isPending}>
        {update.isPending ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Salvando...</> : "Salvar aparência"}
      </Button>
    </form>
  );
}

// ── CategoryDialog ────────────────────────────────────────────────────────────

type CatForm = { handle: string; name: string; hero_title: string; hero_subtitle: string };

function CategoryDialog({
  open, onOpenChange, handle, initial, categoryId,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  handle: string;
  initial?: ShopCategory;
  categoryId?: string;
}) {
  const [form, setForm] = useState<CatForm>({
    handle: initial?.handle ?? "",
    name: initial?.name ?? "",
    hero_title: initial?.hero_title ?? "",
    hero_subtitle: initial?.hero_subtitle ?? "",
  });

  const createCat = useCreateCategory(handle);
  const updateCat = useUpdateCategory(handle);
  const uploadImg = useUploadCategoryImage(handle);
  const isEdit = !!initial;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEdit && categoryId) {
      updateCat.mutate(
        { id: categoryId, name: form.name, hero_title: form.hero_title, hero_subtitle: form.hero_subtitle },
        {
          onSuccess: () => { toast.success("Categoria atualizada!"); onOpenChange(false); },
          onError: (err) => toast.error(apiErr(err)),
        },
      );
    } else {
      createCat.mutate(
        { handle: form.handle, name: form.name, hero_title: form.hero_title, hero_subtitle: form.hero_subtitle },
        {
          onSuccess: () => { toast.success("Categoria criada!"); onOpenChange(false); },
          onError: (err) => toast.error(apiErr(err)),
        },
      );
    }
  };

  const isPending = createCat.isPending || updateCat.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? "Editar Categoria" : "Nova Categoria"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {!isEdit && (
              <div className="space-y-2">
                <Label>Handle</Label>
                <Input
                  placeholder="ex: camisetas"
                  value={form.handle}
                  onChange={(e) => setForm({ ...form, handle: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                  required
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                placeholder="Ex: Camisetas"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Título</Label>
                <Input value={form.hero_title} onChange={(e) => setForm({ ...form, hero_title: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Subtítulo</Label>
                <Input value={form.hero_subtitle} onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })} />
              </div>
            </div>
            {isEdit && categoryId && (
              <ImageUpload
                label="Imagem da categoria"
                currentUrl={initial?.image_url}
                loading={uploadImg.isPending}
                onFile={(f) => uploadImg.mutate({ id: categoryId, file: f }, {
                  onSuccess: () => toast.success("Imagem atualizada!"),
                  onError: (err) => toast.error(apiErr(err)),
                })}
              />
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isEdit ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── CategoriasTab ─────────────────────────────────────────────────────────────

function CategoriasTab({ handle }: { handle: string }) {
  const { data: categories = [], isLoading } = useShopCategories(handle);
  const deleteCat = useDeleteCategory(handle);
  const reorder = useReorderCategories(handle);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ShopCategory | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ShopCategory | null>(null);

  const sorted = [...categories].sort((a, b) => a.position - b.position);

  const move = (idx: number, dir: -1 | 1) => {
    const next = [...sorted];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    reorder.mutate(next.map((c) => c.id), {
      onError: (err) => toast.error(apiErr(err)),
    });
  };

  if (isLoading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Carregando...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{sorted.length} {sorted.length === 1 ? "categoria" : "categorias"}</p>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />Nova Categoria
        </Button>
      </div>

      {sorted.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Nenhuma categoria ainda. Crie a primeira para organizar os produtos.
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {sorted.map((cat, idx) => (
          <Card key={cat.id}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-md border bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                {cat.image_url
                  ? <img src={cat.image_url} alt={cat.name} className="h-full w-full object-cover" />
                  : <ImageIcon className="h-4 w-4 text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{cat.name}</p>
                <p className="text-xs text-muted-foreground">/{cat.handle}</p>
              </div>
              {!cat.active && <Badge variant="secondary">Inativa</Badge>}
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => move(idx, -1)} disabled={idx === 0}>
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => move(idx, 1)} disabled={idx === sorted.length - 1}>
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(cat); setDialogOpen(true); }}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDeleteTarget(cat)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <CategoryDialog
        key={editing?.id ?? "new"}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        handle={handle}
        initial={editing ?? undefined}
        categoryId={editing?.id}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir categoria?</AlertDialogTitle>
            <AlertDialogDescription>
              A categoria <strong>{deleteTarget?.name}</strong> será excluída permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!deleteTarget) return;
                deleteCat.mutate(deleteTarget.id, {
                  onSuccess: () => { toast.success("Categoria excluída."); setDeleteTarget(null); },
                  onError: (err) => toast.error(apiErr(err)),
                });
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── ProductGallerySlots ───────────────────────────────────────────────────────

function ProductGallerySlots({ productId, handle, initialImages }: {
  productId: string;
  handle: string;
  initialImages: ShopProductImage[];
}) {
  const { data: products = [] } = useShopProducts(handle);
  const uploadGallery = useUploadProductGalleryImage(handle);
  const deleteGallery = useDeleteProductGalleryImage(handle);
  const reorderGallery = useReorderProductGallery(handle);
  const fileRef = useRef<HTMLInputElement>(null);

  const images = products.find((p) => p.id === productId)?.images ?? initialImages;
  const slots = Array.from({ length: 5 }, (_, i) => images[i] ?? null);
  const canAdd = images.length < 5;

  function handleUpload(file: File) {
    uploadGallery.mutate({ id: productId, file }, {
      onSuccess: () => toast.success("Imagem adicionada!"),
      onError: (err) => toast.error(apiErr(err)),
    });
  }

  function handleDelete(imageId: string) {
    deleteGallery.mutate({ productId, imageId }, {
      onSuccess: () => toast.success("Imagem removida."),
      onError: (err) => toast.error(apiErr(err)),
    });
  }

  function moveImage(fromIdx: number, toIdx: number) {
    if (toIdx < 0 || toIdx >= images.length) return;
    const newOrder = images.map((img) => img.id);
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    reorderGallery.mutate({ productId, ids: newOrder }, {
      onError: (err) => toast.error(apiErr(err)),
    });
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Fotos do produto</Label>
        <span className="text-xs text-muted-foreground">{images.length} / 5</span>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {slots.map((img, i) => (
          <div key={i} className="relative group aspect-square rounded-md border bg-muted overflow-hidden">
            {img ? (
              <>
                <img src={img.image_url ?? ""} alt={`Foto ${i + 1}`} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
                  <div className="flex gap-1">
                    {i > 0 && (
                      <button
                        type="button"
                        onClick={() => moveImage(i, i - 1)}
                        className="bg-white/20 hover:bg-white/40 text-white rounded p-0.5 text-xs leading-none"
                        title="Mover para esquerda"
                      >‹</button>
                    )}
                    {i < images.length - 1 && (
                      <button
                        type="button"
                        onClick={() => moveImage(i, i + 1)}
                        className="bg-white/20 hover:bg-white/40 text-white rounded p-0.5 text-xs leading-none"
                        title="Mover para direita"
                      >›</button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(img.id)}
                    disabled={deleteGallery.isPending}
                    className="bg-destructive/80 hover:bg-destructive text-white rounded px-1.5 py-0.5 text-[10px]"
                  >
                    Remover
                  </button>
                </div>
                {i === 0 && (
                  <span className="absolute top-1 left-1 text-[9px] bg-black/60 text-white px-1 rounded">capa</span>
                )}
              </>
            ) : (
              canAdd ? (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadGallery.isPending}
                  className="h-full w-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
                >
                  {uploadGallery.isPending
                    ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <Plus className="h-5 w-5" />}
                </button>
              ) : (
                <div className="h-full w-full flex items-center justify-center text-muted-foreground/30">
                  <ImageIcon className="h-4 w-4" />
                </div>
              )
            )}
          </div>
        ))}
      </div>
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(f); e.target.value = ""; }}
      />
      <p className="text-xs text-muted-foreground">A primeira foto é a capa exibida na listagem.</p>
    </div>
  );
}

// ── ProductDialog ─────────────────────────────────────────────────────────────

type ProdForm = {
  name: string;
  slug: string;
  price_points: string;
  description: string;
  description_long: string;
  category_id: string;
  featured: boolean;
};

function ProductDialog({
  open, onOpenChange, handle, initial, categories, onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  handle: string;
  initial?: ShopProduct;
  categories: ShopCategory[];
  onCreated?: (product: ShopProduct) => void;
}) {
  const [form, setForm] = useState<ProdForm>({
    name: initial?.name ?? "",
    slug: initial?.slug ?? "",
    price_points: initial?.price_points?.toString() ?? "",
    description: initial?.description ?? "",
    description_long: initial?.description_long ?? "",
    category_id: initial?.category_id ?? "",
    featured: initial?.featured ?? false,
  });
  const createProd = useCreateProduct(handle);
  const updateProd = useUpdateProduct(handle);
  const isEdit = !!initial;
  const isPending = createProd.isPending || updateProd.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      price_points: parseInt(form.price_points),
      slug: form.slug || undefined,
      description: form.description || undefined,
      description_long: form.description_long || undefined,
      category_id: form.category_id || undefined,
      featured: form.featured,
    };
    if (isEdit) {
      updateProd.mutate(
        { id: initial!.id, ...payload },
        {
          onSuccess: () => { toast.success("Produto atualizado!"); onOpenChange(false); },
          onError: (err) => toast.error(apiErr(err)),
        },
      );
    } else {
      createProd.mutate(payload, {
        onSuccess: (created) => {
          toast.success("Produto criado! Agora adicione as fotos.");
          onOpenChange(false);
          onCreated?.(created);
        },
        onError: (err) => toast.error(apiErr(err)),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEdit ? "Editar Produto" : "Novo Produto"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">

            {/* Nome */}
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                placeholder="Ex: Camiseta Premium"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>

            {/* Slug */}
            <div className="space-y-2">
              <Label>
                Slug <span className="text-muted-foreground font-normal text-xs">(URL da página)</span>
              </Label>
              <Input
                placeholder="camiseta-premium"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
                pattern="^[a-z0-9\-]*$"
              />
            </div>

            {/* Preço + Categoria */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Preço (pontos)</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="500"
                  value={form.price_points}
                  onChange={(e) => setForm({ ...form, price_points: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <select
                  value={form.category_id}
                  onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <option value="">Sem categoria</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Descrição curta */}
            <div className="space-y-2">
              <Label>
                Descrição curta <span className="text-muted-foreground font-normal text-xs">(card da listagem)</span>
              </Label>
              <Textarea
                placeholder="Uma linha resumindo o produto"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </div>

            {/* Descrição completa */}
            <div className="space-y-2">
              <Label>
                Descrição completa <span className="text-muted-foreground font-normal text-xs">(página do produto)</span>
              </Label>
              <Textarea
                placeholder={"Detalhe o produto: duração, ingredientes, indicações...\n\nSepare parágrafos com linha em branco."}
                value={form.description_long}
                onChange={(e) => setForm({ ...form, description_long: e.target.value })}
                rows={6}
              />
            </div>

            {/* Destaque */}
            <div className="flex items-center gap-2">
              <input
                id="featured"
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="h-4 w-4 rounded border-input accent-primary cursor-pointer"
              />
              <Label htmlFor="featured" className="cursor-pointer font-normal">Destaque na loja</Label>
            </div>

            {/* Galeria — só disponível após criar */}
            {isEdit ? (
              <ProductGallerySlots
                productId={initial!.id}
                handle={handle}
                initialImages={initial!.images}
              />
            ) : (
              <p className="text-xs text-muted-foreground border rounded-md p-3 bg-muted/30">
                Salve o produto primeiro para adicionar fotos.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {isEdit ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── ProdutosTab ───────────────────────────────────────────────────────────────

function ProdutosTab({ handle }: { handle: string }) {
  const { data: products = [], isLoading } = useShopProducts(handle);
  const { data: categories = [] } = useShopCategories(handle);
  const deleteProd = useDeleteProduct(handle);
  const updateProd = useUpdateProduct(handle);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ShopProduct | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ShopProduct | null>(null);
  const pendingEditRef = useRef<ShopProduct | null>(null);

  useEffect(() => {
    if (!dialogOpen && pendingEditRef.current) {
      const product = pendingEditRef.current;
      pendingEditRef.current = null;
      const t = setTimeout(() => {
        setEditing(product);
        setDialogOpen(true);
      }, 150);
      return () => clearTimeout(t);
    }
  }, [dialogOpen]);

  const catMap = Object.fromEntries(categories.map((c) => [c.id, c.name]));

  if (isLoading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Carregando...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{products.length} {products.length === 1 ? "produto" : "produtos"}</p>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />Novo Produto
        </Button>
      </div>

      {products.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Nenhum produto ainda. Adicione o primeiro produto à loja.
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {products.map((prod) => (
          <Card key={prod.id}>
            <CardContent className="p-3 flex items-center gap-3">
              <div className="h-10 w-10 rounded-md border bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                {prod.images[0]?.image_url
                  ? <img src={prod.images[0].image_url} alt={prod.name} className="h-full w-full object-cover" />
                  : <ImageIcon className="h-4 w-4 text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{prod.name}</p>
                <p className="text-xs text-muted-foreground">
                  {prod.price_points.toLocaleString("pt-BR")} pts
                  {prod.category_id && catMap[prod.category_id] ? ` · ${catMap[prod.category_id]}` : ""}
                </p>
              </div>
              {prod.featured && <Badge variant="secondary">Destaque</Badge>}
              {!prod.active && <Badge variant="outline" className="text-muted-foreground">Inativo</Badge>}
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7"
                  onClick={() => updateProd.mutate(
                    { id: prod.id, active: !prod.active },
                    {
                      onSuccess: () => toast.success(prod.active ? "Produto desativado." : "Produto ativado."),
                      onError: (err) => toast.error(apiErr(err)),
                    },
                  )}
                >
                  {prod.active ? "Desativar" : "Ativar"}
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(prod); setDialogOpen(true); }}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDeleteTarget(prod)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <ProductDialog
        key={editing?.id ?? "new"}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        handle={handle}
        initial={editing ?? undefined}
        categories={categories}
        onCreated={(p) => { pendingEditRef.current = p; }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir produto?</AlertDialogTitle>
            <AlertDialogDescription>
              O produto <strong>{deleteTarget?.name}</strong> será excluído permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (!deleteTarget) return;
                deleteProd.mutate(deleteTarget.id, {
                  onSuccess: () => { toast.success("Produto excluído."); setDeleteTarget(null); },
                  onError: (err) => toast.error(apiErr(err)),
                });
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── VitrineTab ────────────────────────────────────────────────────────────────

function VitrineTab({ handle }: { handle: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-6 max-w-md">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Vitrine de Produtos</CardTitle>
          <CardDescription>
            Visualize seus produtos em formato de vitrine interativa com carrossel.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ProductVitrineModal
            handle={handle}
            open={open}
            onOpenChange={setOpen}
            trigger={
              <Button className="gap-2">
                <Store className="h-4 w-4" />
                Abrir Vitrine
              </Button>
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ── PublicarTab ───────────────────────────────────────────────────────────────

function PublicarTab({ handle, shopName }: { handle: string; shopName: string }) {
  const deploy = useDeployShop(handle);
  const [deployedUrl, setDeployedUrl] = useState<string | null>(null);
  const [showConsent, setShowConsent] = useState(false);
  const shopUrl = deployedUrl ?? `https://shop.indiqr.com.br/${handle}/`;

  function handleDeploy() {
    setShowConsent(true);
  }

  function handleConfirmDeploy() {
    deploy.mutate(undefined, {
      onSuccess: (data) => {
        setDeployedUrl(data.url);
        setShowConsent(false);
        toast.success("Loja publicada com sucesso!");
      },
      onError: (err) => toast.error(apiErr(err)),
    });
  }

  return (
    <div className="space-y-6 max-w-md">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">URL pública da loja</CardTitle>
          <CardDescription>
            Após publicar, sua loja ficará disponível neste endereço.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 p-3 rounded-md bg-muted text-sm font-mono break-all">
            {shopUrl}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            asChild
          >
            <a href={shopUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4" />
              Abrir loja
            </a>
          </Button>
        </CardContent>
      </Card>

      <Button
        className="gap-2"
        disabled={deploy.isPending}
        onClick={handleDeploy}
      >
        {deploy.isPending
          ? <><Loader2 className="h-4 w-4 animate-spin" />Publicando...</>
          : <><Rocket className="h-4 w-4" />Publicar Loja</>}
      </Button>
      {deploy.isPending && (
        <p className="text-xs text-muted-foreground">Isso pode levar alguns segundos...</p>
      )}

      <DeployConsentModal
        open={showConsent}
        onOpenChange={setShowConsent}
        onConfirm={handleConfirmDeploy}
        shopName={shopName}
        loading={deploy.isPending}
      />
    </div>
  );
}

// ── Shop (page) ───────────────────────────────────────────────────────────────

export default function ShopPage() {
  const { data: shop, isLoading, error } = useMyShop();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const deleteShop = useDeleteShop(shop?.handle ?? "");

  const is404 = (error as { response?: { status?: number } })?.response?.status === 404;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Carregando loja...
      </div>
    );
  }

  if (is404 || !shop) {
    return <CreateShopForm />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <Store className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">{shop.name}</h1>
            <p className="text-sm text-muted-foreground">shop.indiqr.com.br/{shop.handle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!shop.active && <Badge variant="secondary">Inativa</Badge>}
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => { setDeleteConfirmText(""); setShowDelete(true); }}
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Excluir loja
          </Button>
        </div>
      </div>

      <Tabs defaultValue="aparencia">
        <TabsList>
          <TabsTrigger value="aparencia">Aparência</TabsTrigger>
          <TabsTrigger value="categorias">Categorias</TabsTrigger>
          <TabsTrigger value="produtos">Produtos</TabsTrigger>
          <TabsTrigger value="vitrine">Vitrine</TabsTrigger>
          <TabsTrigger value="publicar">Publicar</TabsTrigger>
        </TabsList>

        <TabsContent value="aparencia" className="mt-6">
          <AparenciaTab handle={shop.handle} shop={shop} />
        </TabsContent>

        <TabsContent value="categorias" className="mt-6">
          <CategoriasTab handle={shop.handle} />
        </TabsContent>

        <TabsContent value="produtos" className="mt-6">
          <ProdutosTab handle={shop.handle} />
        </TabsContent>

        <TabsContent value="vitrine" className="mt-6">
          <VitrineTab handle={shop.handle} />
        </TabsContent>

        <TabsContent value="publicar" className="mt-6">
          <PublicarTab handle={shop.handle} shopName={shop.name} />
        </TabsContent>
      </Tabs>

      <Dialog
        open={showDelete}
        onOpenChange={(v) => { if (!v) { setShowDelete(false); setDeleteConfirmText(""); } }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excluir loja?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <p className="text-sm text-muted-foreground">
              Esta ação é <strong>irreversível</strong>. Todos os produtos, categorias e arquivos
              da loja serão permanentemente excluídos.
            </p>
            <p className="text-sm">
              Digite <strong className="font-mono">{shop.handle}</strong> para confirmar:
            </p>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={shop.handle}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowDelete(false); setDeleteConfirmText(""); }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              disabled={deleteConfirmText !== shop.handle || deleteShop.isPending}
              onClick={() => {
                deleteShop.mutate(undefined, {
                  onSuccess: () => {
                    toast.success("Loja excluída.");
                    setShowDelete(false);
                    setDeleteConfirmText("");
                  },
                  onError: (err) => toast.error(apiErr(err)),
                });
              }}
            >
              {deleteShop.isPending
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : "Excluir loja"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
